import { useEffect, useState, useMemo } from 'react'
import { Target, Star, TrendingUp, Globe2, Package } from 'lucide-react'
import { clsx } from 'clsx'
import { useTargetAccountsStore } from './store'
import { useAuthStore } from '@store/authStore'
import { AccountFilters, AccountsTable, ColumnPicker, SignalTracker, AIAssistant, BulkActionBar } from './components'
import type { TargetAccount } from './types'
import { exportAccountsToCSV } from './utils/exportUtils'
import { EmptyState } from '@components/ui/EmptyState'

// Score ordering for sorting
const scoreOrder: Record<string, number> = {
  excellent: 4,
  good: 3,
  medium: 2,
  low: 1,
  high: 3,
}

export function TargetAccountsPage() {
  const {
    accounts,
    owners,
    isLoading,
    error,
    filters,
    sortField,
    sortOrder,
    visibleColumns,
    preferencesLoaded,
    selectedAccountIds,
    loadAccounts,
    loadPreferences,
    setFilter,
    clearFilters,
    setSort,
    selectAccount,
    selectAllAccounts,
    clearSelection,
    bulkSetStatus,
    bulkSetOwner,
  } = useTargetAccountsStore()

  const { user } = useAuthStore()

  const [showFilters, setShowFilters] = useState(false)
  const [selectedAccountForSignals, setSelectedAccountForSignals] = useState<TargetAccount | null>(null)
  const [selectedAccountForAI, setSelectedAccountForAI] = useState<TargetAccount | null>(null)

  // Load accounts on mount
  useEffect(() => {
    loadAccounts()
  }, [loadAccounts])

  // Load preferences when user is authenticated
  useEffect(() => {
    if (user?.email && !preferencesLoaded) {
      loadPreferences(user.email, user.email)
    }
  }, [user?.email, preferencesLoaded, loadPreferences])

  // Get unique sectors
  const sectors = useMemo(() => {
    const unique = [...new Set(accounts.map((a) => a.sector).filter(Boolean))] as string[]
    return unique.sort()
  }, [accounts])

  // Check if any filters are active
  const hasActiveFilters =
    filters.search !== '' ||
    filters.owner !== 'all' ||
    filters.sector !== 'all' ||
    filters.overallScore !== 'all' ||
    filters.status !== 'all'

  // Filter and sort accounts
  const filteredAccounts = useMemo(() => {
    let result = [...accounts]

    // Search filter
    if (filters.search) {
      const query = filters.search.toLowerCase()
      result = result.filter(
        (account) =>
          account.name?.toLowerCase().includes(query) ||
          account.domain?.toLowerCase().includes(query) ||
          account.sector?.toLowerCase().includes(query)
      )
    }

    // Owner filter
    if (filters.owner !== 'all') {
      result = result.filter((account) => account.hubspot_owner_id === filters.owner)
    }

    // Sector filter
    if (filters.sector !== 'all') {
      result = result.filter((account) => account.sector === filters.sector)
    }

    // Overall score filter
    if (filters.overallScore !== 'all') {
      result = result.filter((account) => account.overall_icp_score === filters.overallScore)
    }

    // Status filter
    if (filters.status !== 'all') {
      result = result.filter((account) => {
        const accountStatus = account.account_status || 'active'
        return accountStatus === filters.status
      })
    }

    // Sort
    result.sort((a, b) => {
      let aVal: string | number | null = a[sortField as keyof TargetAccount] as string | null
      let bVal: string | number | null = b[sortField as keyof TargetAccount] as string | null

      // Handle score fields
      if (sortField.includes('score')) {
        aVal = scoreOrder[aVal as string] || 0
        bVal = scoreOrder[bVal as string] || 0
      }

      // Handle strings
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase()
        bVal = bVal.toLowerCase()
      }

      // Handle nulls
      if (aVal == null) aVal = sortOrder === 'asc' ? Infinity : -Infinity
      if (bVal == null) bVal = sortOrder === 'asc' ? Infinity : -Infinity

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1
      } else {
        return aVal < bVal ? 1 : -1
      }
    })

    return result
  }, [accounts, filters, sortField, sortOrder])

  // Stats
  const stats = useMemo(() => {
    return {
      total: accounts.length,
      excellent: accounts.filter((a) => a.overall_icp_score === 'excellent').length,
      good: accounts.filter((a) => a.overall_icp_score === 'good').length,
      medium: accounts.filter((a) => a.overall_icp_score === 'medium').length,
      low: accounts.filter((a) => a.overall_icp_score === 'low').length,
    }
  }, [accounts])

  // Handle stat card click
  const handleStatClick = (score: string) => {
    if (filters.overallScore === score) {
      setFilter('overallScore', 'all')
    } else {
      setFilter('overallScore', score)
    }
  }

  // Handle export of selected accounts
  const handleExportSelected = () => {
    const selectedAccounts = accounts.filter((a) => selectedAccountIds.includes(a.id))
    if (selectedAccounts.length > 0) {
      exportAccountsToCSV(selectedAccounts, owners)
    }
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">Target Accounts</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">ICP-scored accounts for strategic prioritization</p>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-400">Failed to load target accounts: {error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-[var(--text-primary)]">Target Accounts</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">ICP-scored accounts for strategic prioritization</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard
          label="Total Accounts"
          value={stats.total}
          icon={Target}
          color="blue"
          isActive={filters.overallScore === 'all'}
          onClick={() => setFilter('overallScore', 'all')}
        />
        <StatCard
          label="Excellent"
          value={stats.excellent}
          icon={Star}
          color="purple"
          isActive={filters.overallScore === 'excellent'}
          onClick={() => handleStatClick('excellent')}
        />
        <StatCard
          label="Good"
          value={stats.good}
          icon={TrendingUp}
          color="green"
          isActive={filters.overallScore === 'good'}
          onClick={() => handleStatClick('good')}
        />
        <StatCard
          label="Medium"
          value={stats.medium}
          icon={Globe2}
          color="amber"
          isActive={filters.overallScore === 'medium'}
          onClick={() => handleStatClick('medium')}
        />
        <StatCard
          label="Low"
          value={stats.low}
          icon={Package}
          color="slate"
          isActive={filters.overallScore === 'low'}
          onClick={() => handleStatClick('low')}
        />
      </div>

      {/* Filters */}
      <AccountFilters
        filters={filters}
        owners={owners}
        sectors={sectors}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        onFilterChange={setFilter}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Results Summary & Column Picker */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-[var(--text-muted)]">
          Showing {filteredAccounts.length} of {accounts.length} accounts
          {hasActiveFilters && ' (filtered)'}
        </div>
        <ColumnPicker />
      </div>

      {/* Bulk Action Bar */}
      {selectedAccountIds.length > 0 && (
        <BulkActionBar
          selectedCount={selectedAccountIds.length}
          owners={owners}
          onChangeOwner={bulkSetOwner}
          onExportSelected={handleExportSelected}
          onSetStatus={bulkSetStatus}
          onClearSelection={clearSelection}
        />
      )}

      {/* Table */}
      {isLoading ? (
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-[var(--text-muted)]">Loading target accounts...</p>
        </div>
      ) : filteredAccounts.length === 0 ? (
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg">
          <EmptyState
            icon={Target}
            title="No accounts found"
            description="Try adjusting your filters or search query"
          />
        </div>
      ) : (
        <AccountsTable
          accounts={filteredAccounts}
          owners={owners}
          sortField={sortField}
          sortOrder={sortOrder}
          visibleColumns={visibleColumns}
          selectedAccountIds={selectedAccountIds}
          onSort={setSort}
          onSelectAccount={selectAccount}
          onSelectAllAccounts={selectAllAccounts}
          onShowSignals={setSelectedAccountForSignals}
          onShowAIAssistant={setSelectedAccountForAI}
        />
      )}

      {/* Signal Tracker Modal */}
      {selectedAccountForSignals && (
        <SignalTracker
          accountId={selectedAccountForSignals.id}
          accountName={selectedAccountForSignals.name}
          domain={selectedAccountForSignals.domain}
          onClose={() => setSelectedAccountForSignals(null)}
        />
      )}

      {/* AI Assistant Modal */}
      {selectedAccountForAI && (
        <AIAssistant
          accountId={selectedAccountForAI.id}
          accountName={selectedAccountForAI.name}
          domain={selectedAccountForAI.domain}
          onClose={() => setSelectedAccountForAI(null)}
        />
      )}
    </div>
  )
}

// Stat Card Component
interface StatCardProps {
  label: string
  value: number
  icon: React.ElementType
  color: 'blue' | 'purple' | 'green' | 'amber' | 'slate'
  isActive: boolean
  onClick: () => void
}

function StatCard({ label, value, icon: Icon, color, isActive, onClick }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
    green: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    amber: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    slate: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400',
  }

  return (
    <div
      onClick={onClick}
      className={clsx(
        'bg-[var(--bg-secondary)] border rounded-lg p-4 cursor-pointer transition-all',
        isActive
          ? 'border-primary ring-2 ring-primary/20'
          : 'border-[var(--border-color)] hover:border-[var(--text-muted)]'
      )}
    >
      <div className="flex items-center gap-3">
        <div className={clsx('w-10 h-10 rounded-lg flex items-center justify-center', colorClasses[color])}>
          <Icon size={20} />
        </div>
        <div>
          <div className="text-2xl font-bold text-[var(--text-primary)]">{value}</div>
          <div className="text-xs text-[var(--text-muted)]">{label}</div>
        </div>
      </div>
    </div>
  )
}
